import { useState } from 'react'
import { Plus, Search, MessageSquare, Clock, ThumbsUp } from 'lucide-react'

export default function TrainingPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [newQuestion, setNewQuestion] = useState('')

  const handleAskQuestion = () => {
    // Scroll to ask question section or open modal
    const element = document.getElementById('ask-question-box')
    element?.scrollIntoView({ behavior: 'smooth' })
  }

  const handlePostQuestion = () => {
    if (newQuestion.trim()) {
      console.log('Posting question:', newQuestion)
      alert('Question posted successfully!')
      setNewQuestion('')
    } else {
      alert('Please enter a question')
    }
  }

  // Mock student questions
  const questions = [
    {
      id: 1,
      student: 'Sarah Johnson',
      question: 'How do I complete the first exercise in Module 2?',
      course: 'Python Fundamentals',
      category: 'exercise',
      answers: 2,
      helpful: 5,
      timestamp: '2 hours ago',
    },
    {
      id: 2,
      student: 'Mike Chen',
      question: 'Can you explain the difference between lists and tuples?',
      course: 'Python Fundamentals',
      category: 'concept',
      answers: 3,
      helpful: 8,
      timestamp: '4 hours ago',
    },
    {
      id: 3,
      student: 'Lisa Brown',
      question: 'The video in lesson 5 won\'t load. Is there a transcription available?',
      course: 'Advanced Security',
      category: 'technical',
      answers: 1,
      helpful: 2,
      timestamp: '6 hours ago',
    },
    {
      id: 4,
      student: 'John Davis',
      question: 'What\'s the best approach to the capstone project?',
      course: 'Python Fundamentals',
      category: 'project',
      answers: 4,
      helpful: 6,
      timestamp: '1 day ago',
    },
    {
      id: 5,
      student: 'Emma Wilson',
      question: 'How long does this course usually take to complete?',
      course: 'Advanced Security',
      category: 'general',
      answers: 2,
      helpful: 3,
      timestamp: '1 day ago',
    },
  ]

  const filteredQuestions = questions.filter(q => {
    const matchesSearch = q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.course.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || q.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const stats = {
    totalQuestions: questions.length,
    answered: questions.filter(q => q.answers > 0).length,
    unanswered: questions.filter(q => q.answers === 0).length,
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Student Questions</h1>
          <p className="text-blue-200 mt-1">Get help with your training courses - Ask questions and learn from others</p>
        </div>
        <button
          onClick={handleAskQuestion}
          className="btn-primary flex items-center space-x-2 cursor-pointer">
          <Plus size={20} />
          <span>Ask Question</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <div className="card-body">
            <p className="text-blue-200 text-sm">Total Questions</p>
            <p className="text-3xl font-bold text-white mt-2">{stats.totalQuestions}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-blue-200 text-sm">Answered</p>
            <p className="text-3xl font-bold text-green-600 mt-2">{stats.answered}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-blue-200 text-sm">Awaiting Answers</p>
            <p className="text-3xl font-bold text-orange-600 mt-2">{stats.unanswered}</p>
          </div>
        </div>
      </div>

      {/* Ask Question Box */}
      <div className="card" id="ask-question-box">
        <div className="card-body">
          <h3 className="font-semibold text-white mb-4">Ask a Question</h3>
          <textarea
            value={newQuestion}
            onChange={(e) => setNewQuestion(e.target.value)}
            placeholder="Ask a question about your training course..."
            className="input-base mb-3"
            rows="3"
          />
          <div className="flex justify-end">
            <button
              onClick={handlePostQuestion}
              className="btn-primary cursor-pointer">
              Post Question
            </button>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="card">
        <div className="card-body space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-300" size={20} />
            <input
              type="text"
              placeholder="Search questions or courses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-base pl-10 w-full"
            />
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-cyber-600 text-white'
                  : 'bg-slate-700/20 text-blue-200 hover:bg-gray-200'
              }`}
            >
              All Questions
            </button>
            {['concept', 'exercise', 'project', 'technical', 'general'].map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors capitalize ${
                  selectedCategory === cat
                    ? 'bg-cyber-600 text-white'
                    : 'bg-slate-700/20 text-blue-200 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Questions List */}
      {filteredQuestions.length > 0 ? (
        <div className="space-y-3">
          {filteredQuestions.map(q => (
            <div key={q.id} className="card hover:shadow-md transition-shadow cursor-pointer">
              <div className="card-body">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-white text-lg hover:text-cyber-700">
                      {q.question}
                    </h3>
                    <div className="flex items-center space-x-3 mt-2 text-sm text-blue-200">
                      <span className="font-medium">{q.student}</span>
                      <span>•</span>
                      <span className="bg-slate-700/20 px-2 py-1 rounded capitalize text-xs">
                        {q.course}
                      </span>
                      <span>•</span>
                      <span className="text-blue-300/70">{q.timestamp}</span>
                    </div>
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full font-medium capitalize ${
                    q.category === 'concept' ? 'bg-blue-500/20 text-blue-200' :
                    q.category === 'exercise' ? 'bg-green-500/20 text-green-200' :
                    q.category === 'project' ? 'bg-purple-500/20 text-purple-200' :
                    q.category === 'technical' ? 'bg-red-500/20 text-red-200' :
                    'bg-slate-700/20 text-blue-200'
                  }`}>
                    {q.category}
                  </span>
                </div>

                {/* Stats */}
                <div className="flex items-center space-x-4 pt-3 border-t border-blue-500/30 text-sm text-blue-200">
                  <div className="flex items-center space-x-1">
                    <MessageSquare size={16} />
                    <span>{q.answers} answer{q.answers !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <ThumbsUp size={16} />
                    <span>{q.helpful} helpful</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-blue-300/70">No questions found</p>
        </div>
      )}
    </div>
  )
}
